import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../../index.css';
import styles from './AppointmentCalender.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  CircularProgress,
  TextField,
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import axios from 'axios';
import { ApiHeaderWithToken } from '../../common/apisHeaders.js';
import { getPatients } from '../../apis/patientSlice';
import { getPatientHistory } from '../../apis/patientFormSlice';
import { getDoctors } from '../../apis/doctorSlice';
import { toast } from 'react-toastify';
import {
  approveSession,
  createSessionAppointment,
  declineSession,
  deleteAppointment,
  getAppointmentsWithTime,
  getSessionsAppointmentWithTime,
  rescheduleSession,
  updateAppointmentStatus,
} from '../../apis/appointmentSlice';
import SessionAppointmentDetailDialog from './SessionAppointmentDetailDialog.jsx';
const localizer = momentLocalizer(moment);

const getTodayDateString = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const buildSafeDateTime = (date, time, fallbackHour = 9) => {
  const d = new Date(date);

  // If time missing → show default visual block
  if (!time) {
    d.setHours(fallbackHour, 0, 0, 0);
    return d;
  }

  const parts = time.split(':');

  if (parts.length !== 2) {
    d.setHours(fallbackHour, 0, 0, 0);
    return d;
  }

  const [h, m] = parts.map(Number);
  d.setHours(h, m, 0, 0);

  return d;
};

const LegendItem = ({ color, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Box
      sx={{
        width: 14,
        height: 14,
        borderRadius: '3px',
        backgroundColor: color,
      }}
    />
    <Typography variant="body2">{label}</Typography>
  </Box>
);

export default function AppointmentCalender() {
  const dispatch = useDispatch();
  const { appointments, apptLoading, sessions, sessionLoading } = useSelector(
    (state) => state.appointmentData || {},
  );

  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [openAppointmentDetail, setOpenAppointmentDetail] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [sessionDate, setSessionDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');

  const [formData, setFormData] = useState({
    patient: null,
    patientForm: null, // THIS is treatment selection
  });
  const { patients } = useSelector((state) => state.patientData || {});
  const { patientHistory } = useSelector((state) => state.patientFormData || {});
  const [patientAppointments, setPatientAppointments] = useState([]);
  const [sessionsTaken, setSessionsTaken] = useState(0);
  const [nextSessionNo, setNextSessionNo] = useState(1);
  const [selectedSession, setSelectedSession] = useState(null);
  const [openSessionDetail, setOpenSessionDetail] = useState(false);

  const resetScheduleDialog = () => {
    setOpenDialog(false);
    setSelectedSlot(null);
    setFormData({ patient: null, patientForm: null, doctor: null });
    setPatientAppointments([]);
    setSessionsTaken(0);
    setNextSessionNo(1);
  };

  useEffect(() => {
    dispatch(getAppointmentsWithTime());
    dispatch(getSessionsAppointmentWithTime());
  }, [dispatch]);

  useEffect(() => {
    if (openDialog) {
      dispatch(getPatients({ page: 0, pageSize: 1000 }));
    }
  }, [openDialog, dispatch]);

  const appointmentEvents = (appointments || [])
    // Filter out incomplete records and also exclude appointment records that are session-root
    // (they are represented separately in `sessions` and would create duplicate events)
    .filter((appt) => appt.patientId && appt.doctorId && !(appt.sessions && appt.sessions.length > 0))
    .map((appt, idx) => {
      const apptDate = new Date(appt.appointmentDate);
      // const [sh, sm] = appt.startTime.split(':').map(Number);
      // const [eh, em] = appt.endTime.split(':').map(Number);

      // const start = new Date(apptDate);
      // start.setHours(sh, sm, 0, 0);

      // const end = new Date(apptDate);
      // end.setHours(eh, em, 0, 0);

      const start = buildSafeDateTime(appt.appointmentDate, appt.startTime, 9);
      const end = buildSafeDateTime(appt.appointmentDate, appt.endTime, 9.5);

      const patientTitle =
        appt.patientId?.name || appt.patientName || (typeof appt.patientId === 'string' ? appt.patientId : 'Unknown');

      return {
        id: appt._id ? `APPT-${appt._id}` : `APPT-${idx}`,
        title: patientTitle,
        start,
        end,
        resource: appt,
        type: 'APPOINTMENT',
      };
    });


  const sessionEvents = (sessions || []).map((session, idx) => {
    // const sDate = new Date(session.sessionDate);
    // const [sh, sm] = session.startTime.split(':').map(Number);
    // const [eh, em] = session.endTime.split(':').map(Number);

    // const start = new Date(sDate);
    // start.setHours(sh, sm, 0, 0);

    // const end = new Date(sDate);
    // end.setHours(eh, em, 0, 0);

    const start = buildSafeDateTime(session.sessionDate, session.startTime, 11);
    const end = buildSafeDateTime(session.sessionDate, session.endTime, 11.5);

    return {
      id: session._id ? `SESS-${session._id}` : `SESS-${idx}`,
      title: `${session.patientName || session.patientId?.name || 'Unknown'} - Session ${session.sessionNo || ''}`,
      start,
      end,
      resource: session,
      type: 'SESSION',
    };
  });

  const events = [...appointmentEvents, ...sessionEvents];

  const isSlotSelectable = (slotInfo) => {
  const now = new Date();

  // Start of today
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const selectedDate = new Date(slotInfo.start);
  selectedDate.setHours(0, 0, 0, 0);

  if (selectedDate < todayStart) {
    return false;
  }

  return true;
};

const dayPropGetter = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const cellDate = new Date(date);
  cellDate.setHours(0, 0, 0, 0);

  if (cellDate < today) {
    return {
      className: 'rbc-day-disabled',
    };
  }

  return {};
};


  // debug: verify both sets are present
  // eslint-disable-next-line no-console

  // const events = (appointments || []).map((appt) => {
  //   const apptDate = appt.appointmentDate ? new Date(appt.appointmentDate) : new Date();
  //   const [sh, sm] = (appt.startTime || '09:00').split(':').map((s) => parseInt(s, 10));
  //   const [eh, em] = (appt.endTime || '10:00').split(':').map((s) => parseInt(s, 10));
  //   const start = new Date(apptDate);
  //   start.setHours(sh, sm, 0, 0);
  //   const end = new Date(apptDate);
  //   end.setHours(eh, em, 0, 0);
  //   return {
  //     title: `${appt.patientId?.name || 'Unknown'} - ${appt.doctorId?.name || ''}`,
  //     start,
  //     end,
  //     resource: appt,
  //   };
  // });

  // const handleSelectEvent = (event) => {
  //   setSelectedAppointment(event.resource);
  //   setOpenAppointmentDetail(true);
  // };

  const handleSelectEvent = (event) => {
    if (event.type === 'SESSION') {
      setSelectedSession(event.resource);
      setOpenSessionDetail(true);
      // Optional: open session detail dialog
      return;
    }

    // Existing appointment behavior
    setSelectedAppointment(event.resource);
    setOpenAppointmentDetail(true);
  };

  const handleApprove = async (id) => {
    setIsUpdating(true);
    const result = await dispatch(updateAppointmentStatus({ id, docApproval: 'approved' }));
    setIsUpdating(false);

    if (result.type && result.type.includes('fulfilled')) {
      toast.success('Appointment approved successfully!');
      setOpenAppointmentDetail(false);
      setSelectedAppointment(null);
      // Refresh the calendar data
      dispatch(getAppointmentsWithTime());
    } else {
      toast.error(result.payload?.message || 'Failed to approve appointment');
    }
  };

  const handleDecline = async (id) => {
    setIsUpdating(true);
    const result = await dispatch(updateAppointmentStatus({ id, docApproval: 'rejected' }));
    setIsUpdating(false);

    if (result.type && result.type.includes('fulfilled')) {
      toast.success('Appointment declined successfully!');
      setOpenAppointmentDetail(false);
      setSelectedAppointment(null);
      // Refresh the calendar data
      dispatch(getAppointmentsWithTime());
    } else {
      toast.error(result.payload?.message || 'Failed to decline appointment');
    }
  };

  //   const handleSelectSlot = (slotInfo) => {
  //   // slotInfo.start = clicked datetime
  //   // slotInfo.end = auto calculated by calendar

  //   setSelectedSlot(slotInfo);
  //   setOpenDialog(true);
  // };
  const handleSelectSlot = (slotInfo) => {
    const now = new Date();

    // Create "start of today" (00:00:00)
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);

    // Compare only the date part of the selected slot
    const selectedDateStart = new Date(slotInfo.start);
    selectedDateStart.setHours(0, 0, 0, 0);

    if (selectedDateStart < todayStart) {
      toast.error('Cannot select past dates. Today or future dates only.');
      return; // dialog does NOT open
    }
    setSelectedSlot(slotInfo);

    // Prefill editable fields (LOCAL SAFE)
    setSessionDate(formatDateLocal(slotInfo.start));
    setStartTime(formatTimeLocal(slotInfo.start));
    setEndTime(formatTimeLocal(slotInfo.end));

    setOpenDialog(true);
  };

  const formatDateLocal = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatTimeLocal = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const handleSaveSession = async () => {
    if (!formData.patient || !formData.patientForm) {
      toast.error('Please select a patient and a patient form (treatment)');
      return;
    }
    // Validate times and date
    if (!sessionDate) {
      toast.error('Please select a date');
      return;
    }

    // prevent past date
    const todayStr = getTodayDateString();
    if (sessionDate < todayStr) {
      toast.error('Cannot schedule for past dates');
      return;
    }

    if (startTime && endTime) {
      const startDT = new Date(`${sessionDate}T${startTime}`);
      const endDT = new Date(`${sessionDate}T${endTime}`);
      if (startDT >= endDT) {
        toast.error('Start time must be earlier than end time');
        return;
      }
    }

    

    const payload = {
      patientId: formData.patient._id || formData.patient,
      patientName: formData.patient.name || '',
      sessionDate: sessionDate,
      startTime: startTime,
      endTime: endTime,
      // include patientFormId so appointment links to the form
      patientFormId: formData.patientForm._id,
    };

    const result = await dispatch(createSessionAppointment(payload));
    if (result.type && result.type.includes('fulfilled')) {
      toast.success('Session appointment created');
      setOpenDialog(false);
      setFormData({ patient: null, patientForm: null, doctor: null });
      setPatientAppointments([]);
      setSessionsTaken(0);
      setNextSessionNo(1);
      dispatch(getSessionsAppointmentWithTime());
    } else {
      toast.error(result.payload?.message || 'Failed to create appointment');
    }
  };

  const handlePatientSelect = async (patient) => {
    setFormData((s) => ({ ...s, patient, patientForm: null }));
    if (!patient) return;
    // fetch patient forms
    await dispatch(getPatientHistory(patient._id));
    // fetch appointments for this patient (not touching global state)
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_BACKEND_API}/appointment/getAppointmentsByPatient`,
        { params: { patientId: patient._id }, ...ApiHeaderWithToken() },
      );
      setPatientAppointments(resp.data.data || []);
    } catch (e) {
      setPatientAppointments([]);
    }
  };

  const getPatientFormLabel = (opt) => {
    if (opt.formType === 'PHYSIO') {
      // return opt.formData?.treatment;
      return;
    } else if (opt.formType === 'ESTHETIC') {
      return opt.formData?.estheticsQuestions?.skinConcern;
    } else if (opt.formType === 'DENTAL') {
      return opt.formData?.dentalQuestions?.specificConcern;
    }
    return 'Untitled Form';
  };

  const filteredPatientHistory = (patientHistory || []).filter((opt) => {
    const label = getPatientFormLabel(opt);
    return label && label !== 'Untitled Form';
  });

  const handlePatientFormSelect = (form) => {
    setFormData((s) => ({ ...s, patientForm: form }));
    if (!form) {
      setSessionsTaken(0);
      setNextSessionNo(1);
      return;
    }

    const related = (patientAppointments || []).filter(
      (a) => String(a.patientFormId) === String(form._id),
    );
    let max = 0;
    related.forEach((a) => {
      (a.sessions || []).forEach((s) => {
        const sn = Number(s.sessionNo) || 0;
        if (sn > max) max = sn;
      });
    });
    setSessionsTaken(max);
    setNextSessionNo(max + 1);
  };

  const handleCloseSession = () => {
    setOpenSessionDetail(false);
    setSelectedSession(null);
  };

  // const handleSessionApprove = async (session) => {
  //   await dispatch(updateAppointmentStatus({
  //     id: session._id,
  //     status: 'approved',
  //   }));

  //   dispatch(getSessionsAppointmentWithTime());
  //   handleCloseSession();
  // };

  const handleSessionApprove = async (session) => {

    
    const result = await dispatch(
      approveSession({
        appointmentId: session.appointmentId,
        sessionNo: session.sessionNo,
        isSessionTaken: session.sessionType == 'package' ? true : undefined,
      }),
    );

    if (result.type.includes('fulfilled')) {
      toast.success('Session Approved');
      dispatch(getSessionsAppointmentWithTime());
      handleCloseSession();
    } else {
      toast.error('Failed to approve session');
    }
  };

  const handleSessionDecline = async (session) => {
    const result = await dispatch(
      declineSession({
        appointmentId: session.appointmentId,
        sessionNo: session.sessionNo,
        reason: 'Doctor Declined',
      }),
    );

    if (result.type.includes('fulfilled')) {
      toast.error('Session Cancelled');
      dispatch(getSessionsAppointmentWithTime());
      handleCloseSession();
    } else {
      toast.error('Failed to decline session');
    }
  };

  // const handleSessionDecline = async (session) => {
  //   await dispatch(updateAppointmentStatus({
  //     id: session._id,
  //     status: 'rejected',
  //   }));

  //   dispatch(getSessionsAppointmentWithTime());
  //   handleCloseSession();
  // };

  // const handleSessionReschedule = async (updatedSession) => {
  //   await dispatch(rescheduleSession(updatedSession)); // your thunk

  //   dispatch(getSessionsAppointmentWithTime());
  //   handleCloseSession();
  // };
  const handleSessionReschedule = async (updatedSession) => {
    if (!updatedSession.sessionDate || !updatedSession.startTime || !updatedSession.endTime) {
      toast.error('All fields are mandatory');
      return;
    }
    // prevent past date
    const todayStr = getTodayDateString();
    if (updatedSession.sessionDate < todayStr) {
      toast.error('Cannot reschedule to a past date');
      return;
    }

    // ensure start < end
    const startDT = new Date(`${updatedSession.sessionDate}T${updatedSession.startTime}`);
    const endDT = new Date(`${updatedSession.sessionDate}T${updatedSession.endTime}`);
    if (startDT >= endDT) {
      toast.error('Start time must be earlier than end time');
      return;
    }
    const result = await dispatch(
      rescheduleSession({
        appointmentId: updatedSession.appointmentId,
        sessionNo: updatedSession.sessionNo,
        newDate: updatedSession.sessionDate,
        newStartTime: updatedSession.startTime,
        newEndTime: updatedSession.endTime,
      }),
    );

    if (result.type.includes('fulfilled')) {
      toast.info('Session Rescheduled');
      dispatch(getSessionsAppointmentWithTime());
      handleCloseSession();
    } else {
      toast.error('Failed to reschedule');
    }
  };

  return (
    <div className={styles.page}>
      <Box className={styles.topBar}>
        <Box className={styles.titleBox}>
          <Typography component="h2">Appointment Calendar</Typography>
        </Box>

        <Box className={styles.legendCard}>
          <LegendItem color="#6a1b9a" label="Session Treatment" />
          <LegendItem color="#4caf50" label="Dental Appointment" />
          <LegendItem color="#191c63" label="Other Appointment" />
        </Box>
      </Box>


      {(apptLoading || sessionLoading) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress />
        </Box>
      )}

      <Box className={`${styles.calendarCard} ${styles.rbcScope}`}>
        <div className={styles.calendarContainer}>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            onSelectEvent={handleSelectEvent}
            onSelectSlot={handleSelectSlot}
            selectable={isSlotSelectable}
            dayPropGetter={dayPropGetter}
        // eventPropGetter={(event) => {
        //   const speciality =
        //     event?.resource?.doctorId?.docSpeciality?.name?.toLowerCase() || '';

        //   return {
        //     className:
        //       speciality === 'dentist'
        //         ? 'rbc-dentist-event'
        //         : 'rbc-other-event',
        //   };
        // }}

            eventPropGetter={(event) => {
              if (event.type === 'SESSION') {
                return {
                  style: {
                    backgroundColor: '#6a1b9a',
                    color: '#fff',
                    borderRadius: '6px',
                    border: 'none',
                  },
                };
              }

              const speciality = event?.resource?.doctorId?.docSpeciality?.name?.toLowerCase() || '';

              return {
                className: speciality === 'dentist' ? 'rbc-dentist-event' : 'rbc-other-event',
              };
            }}
          />
        </div>
      </Box>
      <Dialog
        open={openAppointmentDetail}
        onClose={() => setOpenAppointmentDetail(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Appointment Request</DialogTitle>
        <DialogContent>
          {selectedAppointment && (
            <Box sx={{ mt: 2, display: 'flex', gap: 2, flexDirection: 'column' }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Doctor Information
                </Typography>
                <Typography variant="body2">
                  <strong>Name:</strong> {selectedAppointment.doctorId?.name || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Speciality:</strong>{' '}
                  {selectedAppointment.doctorId?.docSpeciality?.name || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Patient Information
                </Typography>
                <Typography variant="body2">
                  <strong>Name:</strong> {selectedAppointment.patientId?.name || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Phone:</strong> {selectedAppointment.patientId?.phone || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Appointment Information
                </Typography>
                <Typography variant="body2">
                  <strong>Date:</strong>{' '}
                  {selectedAppointment.appointmentDate
                    ? moment(selectedAppointment.appointmentDate).format('LL')
                    : 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Time:</strong> {selectedAppointment.startTime} -{' '}
                  {selectedAppointment.endTime}
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong>
                  <Chip
                    label={selectedAppointment.docApproval || 'pending'}
                    color={
                      selectedAppointment.docApproval === 'approved'
                        ? 'success'
                        : selectedAppointment.docApproval === 'rejected'
                          ? 'error'
                          : 'default'
                    }
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedAppointment && selectedAppointment.docApproval === 'pending' && (
            <>
              <Button
                onClick={() => handleApprove(selectedAppointment._id)}
                color="success"
                variant="contained"
                disabled={isUpdating}
              >
                {isUpdating ? <CircularProgress size={20} /> : 'Approve'}
              </Button>
              <Button
                onClick={() => handleDecline(selectedAppointment._id)}
                color="error"
                variant="contained"
                disabled={isUpdating}
              >
                {isUpdating ? <CircularProgress size={20} /> : 'Decline'}
              </Button>
            </>
          )}
          {selectedAppointment && selectedAppointment.docApproval !== 'pending' && (
            <Box>
              <Typography variant="body2" color="textSecondary">
                Status:{' '}
                <Chip
                  label={selectedAppointment.docApproval}
                  color={selectedAppointment.docApproval === 'approved' ? 'success' : 'error'}
                  size="small"
                />
              </Typography>
            </Box>
          )}
          <Button onClick={() => setOpenAppointmentDetail(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDialog}
        onClose={resetScheduleDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: 2, border: '1px solid #d7e2ef' },
        }}
      >
        <DialogTitle className={styles.scheduleDialogTitle}>Schedule Session</DialogTitle>
        <DialogContent className={styles.scheduleDialogContent}>
          <Autocomplete
            options={patients || []}
            getOptionLabel={(opt) => `${opt.name} - ${opt.phone}` || ''}
            value={formData.patient}
            disableClearable
            onChange={(e, v) => v && handlePatientSelect(v)}
            renderInput={(params) => (
              <TextField {...params} label="Patient" margin="dense" size="small" required />
            )}
          />

          <Autocomplete
            options={filteredPatientHistory}
            getOptionLabel={getPatientFormLabel}
            value={formData.patientForm}
            disableClearable
            onChange={(e, v) => v && handlePatientFormSelect(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Patient Form / Treatment"
                margin="dense"
                size="small"
                required
              />
            )}
          />

          <div className={styles.sessionMetaRow}>
            <TextField
              label="Date"
              required
              type="date"
              fullWidth
              size="small"
              margin="dense"
              value={sessionDate}
              onChange={(e) => setSessionDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: getTodayDateString() }}
              disabled={Boolean(selectedSlot)}
            />

            <TextField
              label="Start Time"
              required
              type="time"
              fullWidth
              size="small"
              margin="dense"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </div>

          <TextField
            required
            label="End Time"
            type="time"
            fullWidth
            size="small"
            margin="dense"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          {/* <Autocomplete
          options={doctors || []}
          getOptionLabel={(opt) => opt.name || ''}
          value={formData.doctor || null}
          onChange={(e, v) => setFormData((s) => ({ ...s, doctor: v }))}
          renderInput={(params) => <TextField {...params} label="Doctor" margin="normal" />}
        /> */}

          <TextField
            label="Sessions Taken"
            fullWidth
            margin="dense"
            size="small"
            value={sessionsTaken}
            disabled
          />
          <TextField
            label="Next Session No"
            fullWidth
            margin="dense"
            size="small"
            value={nextSessionNo}
            disabled
          />
        </DialogContent>

        <DialogActions className={styles.scheduleDialogActions}>
          <Button
            className={styles.cancelBtn}
            onClick={resetScheduleDialog}
            variant="outlined"
            color="inherit"
            size="small"
          >
            Cancel
          </Button>
          <Button
            className={styles.saveBtn}
            variant="contained"
            size="small"
            onClick={handleSaveSession}
            disabled={!formData.patient || !formData.patientForm || !sessionDate || !startTime || !endTime}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <SessionAppointmentDetailDialog
        open={openSessionDetail}
        session={selectedSession}
        onClose={handleCloseSession}
        onApprove={handleSessionApprove}
        onDecline={handleSessionDecline}
        onReschedule={handleSessionReschedule}
      />
    </div>
  );
}
